/* eslint max-lines: ["error", { max: 330 }] -- this overrides for the entire file */
/* eslint complexity: ["error", { max: 7 }] -- this overrides for the entire file */
import { format } from "date-fns";
import isNil from "lodash/isNil";

import { DateFormat } from "@/constants/DateFormat";
import type { AvatarCardProps } from "@/ui/core/AvatarCard";
import {
  getDateRange,
  getEndTime,
  getTimeRange,
  sortDateComparator,
} from "@/utils/date.util";
import { doesRecur } from "@/utils/schedule.util";
import { isNotNilNotEmpty } from "@/utils/shared.utils";
import { getPreferredFullName } from "@/utils/user.util";

const PreviewCardCount = 6;

const EventRecurrenceDisplayString: Record<EventRecurrence, string> = {
  Biweekly: "Every other",
  "Does not repeat": "",
  Monthly: "Every 4th",
  Weekly: "Every",
};

const shouldHideRegistrationStatus = ({
  isAppUserSupport,
  isSensitive,
}: {
  isAppUserSupport?: boolean;
  isSensitive?: boolean;
}) => {
  return isSensitive && isAppUserSupport;
};

const getEventRecurrenceDisplayString = (
  frequency: EventRecurrence,
): null | string => {
  const displayString = EventRecurrenceDisplayString[frequency];
  return isNil(displayString) ? null : displayString;
};

const getGroupClassRecurrenceDisplayString = ({
  durationMinutes,
  frequency,
  startDate,
}: {
  durationMinutes: number;
  frequency: EventRecurrence;
  startDate: string;
}): string => {
  const recurrence = getEventRecurrenceDisplayString(frequency);
  const weekday = format(new Date(startDate), DateFormat.weekDay);
  const endTime = getEndTime({ durationMinutes, startDate });
  const timeRange = getTimeRange({
    endDate: endTime?.toString(),
    shouldIncludeStartAmPm: !endTime,
    startDate,
  });

  const spacedRecurrence = recurrence ? `${recurrence} ` : "";
  return `${spacedRecurrence}${weekday}, ${timeRange}`;
};

const shouldHaveRecurrenceDisplayString = (groupClass: GroupClass): boolean => {
  // only have a recurrence display string if there is a start date in the future
  const hasNextStartDate = groupClass.nextStartDate !== undefined;
  const hasFutureStartDate = new Date(groupClass.startDate) > new Date();
  return hasNextStartDate || hasFutureStartDate;
};

const getRecurrenceDisplayDate = (groupClass: GroupClass): string => {
  // nextStartDate is optional and does not exist for "Does Not Repeat" group classes
  // startDate is guaranteed to exist, but is only relevant if there is no next start date
  return groupClass.nextStartDate || groupClass.startDate;
};

const toGroupClassSeries = (groupClass: GroupClass): GroupClassSeries => {
  return {
    ...groupClass,
    recurrenceDisplayString: shouldHaveRecurrenceDisplayString(groupClass)
      ? getGroupClassRecurrenceDisplayString({
          durationMinutes: groupClass.duration,
          frequency: groupClass.frequency,
          startDate: getRecurrenceDisplayDate(groupClass),
        })
      : "",
  };
};

const isUserRegisteredForSensitiveClass = ({
  groupClass,
  shouldShowSensitiveRegistration,
}: {
  groupClass: GroupClass;
  shouldShowSensitiveRegistration: boolean;
}) => {
  const { isSensitive, isUserRegistered } = groupClass;
  if (shouldShowSensitiveRegistration) {
    return isUserRegistered;
  }
  // hide sensitive group class's registration status as forced to 'false'
  return isSensitive ? false : isUserRegistered;
};

const sortGroupClassesByRegistrationStatusAndStartDate = ({
  groupClasses,
  shouldShowSensitiveRegistration,
}: {
  groupClasses: GroupClass[];
  shouldShowSensitiveRegistration: boolean;
}): GroupClass[] => {
  const hideSensitiveRegistration = (groupClass: GroupClass) =>
    isUserRegisteredForSensitiveClass({
      groupClass,
      shouldShowSensitiveRegistration,
    });

  // eslint-disable-next-line max-params
  return groupClasses.slice().sort((first, second) => {
    const isFirstRegistered = hideSensitiveRegistration(first);
    const isSecondRegistered = hideSensitiveRegistration(second);

    if (isFirstRegistered !== isSecondRegistered) {
      // sort so that all date-ascending registered classes come first,
      // followed by date-asscending unregistered classes
      return Number(isSecondRegistered) - Number(isFirstRegistered);
    }

    return sortDateComparator({
      first: getRecurrenceDisplayDate(first),
      second: getRecurrenceDisplayDate(second),
    });
  });
};

const transformGroupClassesToGroupClassSeries = ({
  groupClasses,
  patientId,
}: {
  groupClasses: GroupClass[];
  patientId?: string;
}): GroupClassSeries[] => {
  // if patientId is not provided, we assume we're fetching group classes for the current user
  // in this case, we want to include sensitive group classes, in the sorting criteria
  const shouldShowSensitiveRegistration = !isNotNilNotEmpty(patientId);
  const sortedGroupClasses = sortGroupClassesByRegistrationStatusAndStartDate({
    groupClasses,
    shouldShowSensitiveRegistration,
  });
  return sortedGroupClasses.map(toGroupClassSeries);
};

const getAttendeeFromHost = (host: GroupClassSeriesHost): AvatarCardProps => {
  const {
    externalId: userId,
    firstName,
    lastName,
    pronouns,
    providerType,
    type: userType,
  } = host;

  return {
    pronouns,
    subtitle: providerType,
    userId,
    userName: getPreferredFullName({
      firstName,
      lastName,
    }),
    userType,
  };
};

const toDetailsProps = ({
  groupClass,
  isAppUserSupport,
}: {
  groupClass: GroupClassSeries;
  isAppUserSupport: boolean;
}): Omit<
  GroupClassSeriesDetailsProps,
  "isUserPreTreatment" | "onEditEventPress"
> => {
  const {
    description,
    endDate,
    frequency,
    groupClassExternalId: externalId,
    hosts,
    iconName,
    isSensitive,
    isUserRegistered,
    nextStartDate,
    recurrenceDisplayString,
    startDate,
    title,
  } = groupClass;

  const attendees = hosts?.map(getAttendeeFromHost) || [];

  const isRecurring = doesRecur(frequency);
  const [recurrenceString, timeRange] = recurrenceDisplayString.split(", ");
  const dateRange = getDateRange({ endDate, startDate });

  const nextDate = nextStartDate
    ? format(new Date(nextStartDate), DateFormat.weekDayShortMonthDay)
    : undefined;

  const displayStartDate = isRecurring
    ? undefined
    : format(new Date(startDate), DateFormat.weekDayShortMonthDay);

  return {
    attendees,
    dateRange,
    description,
    externalId,
    iconName,
    isAppUserSupport,
    isRecurring,
    isSensitive,
    isUserRegistered,
    nextStartDate: nextDate,
    recurrenceString,
    startDate: displayStartDate,
    timeRange,
    title,
  };
};

const toPreviewProps = (
  groupClass: GroupClassSeries,
): Omit<GroupClassSeriesPreviewCardProps, "onSelect"> => {
  const {
    groupClassExternalId,
    iconName,
    isUserRegistered,
    recurrenceDisplayString,
    title,
  } = groupClass;
  const [recurrenceString, timeRange] = recurrenceDisplayString.split(", ");
  return {
    groupClassExternalId,
    iconName,
    isUserRegistered,
    recurrenceString,
    timeRange,
    title,
  };
};

const getRelevantGroupClasses = ({
  groupClasses: initialGroupClasses,
  shouldShowSensitiveRegistration,
  variant,
}: {
  groupClasses?: GroupClassSeries[];
  shouldShowSensitiveRegistration: boolean;
  variant: GroupClassCarouselVariant;
}) => {
  if (isNil(initialGroupClasses)) {
    return [];
  }

  // mask sensitive status up front to ensure masked groups are filtered accordingly
  const groupClasses = initialGroupClasses.map((groupClass) => ({
    ...groupClass,
    isUserRegistered: isUserRegisteredForSensitiveClass({
      groupClass,
      shouldShowSensitiveRegistration,
    }),
  }));

  // Record type to enforce that every possible variant type is handled
  const RelevantGroupClassHandlers: Record<
    GroupClassCarouselVariant,
    () => GroupClassSeries[]
  > = {
    basicGroups: () => {
      // for "basicGroups" variant: show GroupClasses carousel for user,
      // cut off to PreviewCardCount many, of classes user is not registered for
      return groupClasses
        .filter((gc) => !gc.isUserRegistered)
        .slice(0, PreviewCardCount);
    },
    promotedGroups: () => {
      // show promoted group classes, as specified by the api,
      // also that the user is not already registered for
      return groupClasses
        .filter((gc) => gc.isPromoted)
        .filter((gc) => !gc.isUserRegistered);
    },
    yourGroups: () => {
      // show *all* group classes that the user is registered for
      return groupClasses.filter((gc) => gc.isUserRegistered);
    },
  };

  const relevantHandler = RelevantGroupClassHandlers[variant];
  return relevantHandler();
};

export {
  getAttendeeFromHost,
  getEventRecurrenceDisplayString,
  getGroupClassRecurrenceDisplayString,
  getRecurrenceDisplayDate,
  getRelevantGroupClasses,
  PreviewCardCount,
  shouldHaveRecurrenceDisplayString,
  shouldHideRegistrationStatus,
  sortGroupClassesByRegistrationStatusAndStartDate,
  toDetailsProps,
  toGroupClassSeries,
  toPreviewProps,
  transformGroupClassesToGroupClassSeries,
};
